// src/components/settings/catalog/CatalogExport.js
// CATALOG SCREEN VERIFIER: catscreen-7a2c4e91 — Settings › Catalog, Stage 4.
//
// Export. The server builds the file (exportCatalog: the importer's columns, Spatchy ID first,
// spreadsheet formula guard) and hands back the CSV text itself; the browser saves it
// (catroll-5c8e2a71 — nothing stored, no link).
//   whole catalog: "Include archived items" tick (off by default), then Export;
//   selected / all matching: starts straight away.
import React, { useEffect, useRef, useState } from 'react';
import { Loader2, CheckCircle2 } from 'lucide-react';
import { exportCatalog, downloadCsv, friendlyError } from './catalogApi';
import { BTN_GHOST, BTN_PRIMARY, BTN_LINK, Dialog, ErrorLine } from './catalogUiParts';

const EXPORT_ERROR = "Couldn't prepare your file. Try again.";

/** selection: null (whole catalog) | { kind: 'items', items } | { kind: 'filter', filter } */
export default function ExportDialog({ open, selection, label, onClose }) {
    const whole = !selection;
    const [includeArchived, setIncludeArchived] = useState(false);
    const [st, setSt] = useState({ phase: whole ? 'choose' : 'preparing', error: '', csv: null, fileName: null, rows: 0 });
    const started = useRef(false);

    const run = async () => {
        setSt({ phase: 'preparing', error: '', csv: null, fileName: null, rows: 0 });
        try {
            const r = await exportCatalog(whole ? { includeArchived } : { selection });
            setSt({ phase: 'ready', error: '', csv: r.csv, fileName: r.fileName, rows: r.rows });
            downloadCsv(r.csv, r.fileName);
        } catch (err) {
            console.error('Catalog: export failed', err);
            setSt({ phase: 'error', error: friendlyError(err, EXPORT_ERROR), csv: null, fileName: null, rows: 0 });
        }
    };
    useEffect(() => {
        if (open && !whole && !started.current) { started.current = true; run(); }
    }, [open]); // eslint-disable-line react-hooks/exhaustive-deps

    const busy = st.phase === 'preparing';
    return (
        <Dialog open={open} title={whole ? 'Export catalog' : `Export ${label}`} onClose={onClose} busy={busy}
            footer={(
                <>
                    <button type="button" className={BTN_GHOST} disabled={busy} onClick={onClose}>{st.phase === 'ready' ? 'Close' : 'Cancel'}</button>
                    {(st.phase === 'choose' || st.phase === 'error') && <button type="button" className={BTN_PRIMARY} onClick={run}>{st.phase === 'error' ? 'Try again' : 'Export'}</button>}
                </>
            )}>
            {st.phase === 'choose' && (
                <>
                    <p className="text-sm text-gray-700">A spreadsheet file (CSV) of your catalog, with costs and suppliers. It opens in Excel or Google Sheets, and it's in the layout Spatchy can import later.</p>
                    <label className="flex items-center gap-3 min-h-[44px] text-sm text-gray-800 cursor-pointer">
                        <input type="checkbox" className="w-5 h-5 accent-[#5F4EC9]" checked={includeArchived} onChange={(e) => setIncludeArchived(e.target.checked)} />
                        Include archived items
                    </label>
                </>
            )}
            {busy && <p className="text-sm text-gray-700 flex items-center gap-2" aria-live="polite"><Loader2 size={16} className="animate-spin text-[#5F4EC9]" /> Preparing your file…</p>}
            {st.phase === 'ready' && (
                <div className="space-y-2">
                    <p className="text-sm text-green-800 flex items-center gap-2"><CheckCircle2 size={16} /> Your file is ready: {st.rows.toLocaleString('en-US')} item{st.rows === 1 ? '' : 's'}.</p>
                    <p className="text-sm text-gray-600">
                        If it didn't download, <button type="button" className={BTN_LINK} onClick={() => downloadCsv(st.csv, st.fileName)}>download it again</button>.
                    </p>
                    <p className="text-xs text-gray-500">The file includes costs and suppliers — share it only with people who should see them.</p>
                </div>
            )}
            {st.phase === 'error' && <ErrorLine text={st.error} />}
        </Dialog>
    );
}
